import { createContext, useReducer, useRef } from "react";
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { format, set } from 'date-fns';
import { API } from "../../../API";
import { ShareAPI } from "../../../API";
import { useTranslation } from 'react-i18next';
import messagesReducer, { initialState } from "./MessagesReducer";
import { useCurrentUser } from "../../../contexts/CurrentUser";
import { languageMapping } from "../../../utils/languages";
import { replaceNewlinesWithBreaks } from "../../../utils/replaceNewlinesWithBreaks";
import { ampli } from "../../../ampli";
import { showRequestError } from '../../../utils/requestError';
import { toApiAffiliationTypes } from '../../../utils/affiliationTypes';

export const MessagesContext = createContext(initialState);

// Narrative search v2 (saqe) vocabulary mappings.
// The frontend's platform values already match the saqe UPPERCASE vocabulary,
// except `WEB`, which saqe expects as `RSS` (SourceType.WEB). All other values
// pass through verbatim.
const NARRATIVE_SEARCH_V2_PLATFORM_MAPPING = {
  WEB: 'RSS',
};

const mapPlatformToV2 = (platform) =>
  NARRATIVE_SEARCH_V2_PLATFORM_MAPPING[platform] || platform;

// The frontend uses the app-side sort aliases (`date_publicated`, `impressions`)
// that v1 translates server-side. saqe's `/search-narrative` only accepts the
// native field names, so translate them here before sending the v2 body.
const NARRATIVE_SEARCH_V2_SORT_MAPPING = {
  '-date_publicated': '-published_at',
  'date_publicated': 'published_at',
  '-impressions': '-views',
  'impressions': 'views',
};

const mapSortToV2 = (sortingQuery) =>
  NARRATIVE_SEARCH_V2_SORT_MAPPING[sortingQuery] || sortingQuery;

export const areAllIdsSelected = (messages, selectedMessages, deduplication) => { 
  if(deduplication) {
    for (const message of messages) {
      if (!(message.id in selectedMessages)) {
        return false;
      }
    }
  } else {
    for (const message of messages) {
      if (!(message.id in selectedMessages)) {
        return false;
      }
    }
  }
  
  return true;
}

export const MessagesProvider = ({isShare, children}) => {
  const [state, dispatch] = useReducer(messagesReducer, initialState);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [currentUser] = useCurrentUser();

  const api = isShare ? ShareAPI : API;

  const isValueSet = (value) => {
    if (Array.isArray(value) && !value.some(isValueSet)) {
      return false;
    }

    return value !== "" && value !== undefined && value !== null;
  }
  const isAnyFilterApplied = (filters) => {
    let filters_copy = { ...filters }

    const values = Object.values(filters_copy);

    return  values.some(isValueSet);
  }

  const abortControllerRef = useRef();
  let translationControllerRef = useRef();
  

  const fetchNarrative = (narrativeId) => {
    const onRequestError = (resp) => {
      if (isShare && (resp.status === 401 || resp.status === 403)) {
        const hash = searchParams.get('h');
        const with_password = searchParams.get('with_password');
        navigate(`/narratives/${narrativeId}/share/login?h=${hash}&with_password=${with_password}`);
      }
    }

    api.fetch('GET', `/API/v1/narratives/${narrativeId}`, null, null, null, onRequestError)
    .then((data) => {
      dispatch({type: 'GET_NARRATIVE', narrative: data})
    })
    .catch((e) => {
      if (e.message === 'Client error: Narrative not found') {
        dispatch({type: 'ERROR', error: 'Narrative not found'})
      } else {
        dispatch({type: 'ERROR', error: t('Failed to fetch narrative')})
      }
    });
  }

  // Consolidate the same filter/keyword/status/favorite/sort/cursor/pagination
  // state that v1 splits between URL query params and the JSON body into a single
  // v2 JSON body, remapping to the v2 (saqe-aligned) field names. Cursor-only:
  // we send `cursor`, never `page`. See `buildV2SearchBody` callers in getMessages.
  const buildV2SearchBody = ({
    narrative,
    searchQuery,
    sortingQuery,
    statuses,
    page_size,
    showOnlyFavorites,
    start,
    end,
    platforms,
    sourceGroups,
    languages,
    sources,
    countries,
    sentiment,
    contentTypes,
    showOnlyBots,
    discreditations,
    affiliationCountries,
    geoGroup,
    languagesExclude,
    countriesExclude,
    sourceGroupsExclude,
    sourcesExclude,
    affiliationCountriesExclude,
    geoGroupExclude,
    botsExclude,
    discreditationsExclude,
    affiliationTypes,
    affiliationTypesExclude,
    cursor,
  }) => {
    const keyword = searchQuery ? searchQuery.replace(/(\r\n|\n|\r)/gm, "") : "";
    const body = {};
    const excludedFilters = [];

    if (keyword) {
      body.keywords = [keyword];
    }

    if (start) {
      body.start_date = format(start, 'yyyy-LL-dd');
    }

    if (end) {
      body.end_date = format(end, 'yyyy-LL-dd');
    }

    if (platforms && platforms.length > 0) {
      body.platforms = platforms.map(mapPlatformToV2);
    }

    if (sentiment && sentiment.length > 0) {
      body.sentiment = sentiment;
    }

    if (sourceGroups && sourceGroups.length > 0) {
      body.source_group_ids = sourceGroups;
    }

    if (languages && languages.length > 0) {
      // v2 (saqe) expects language code strings (e.g. "EN"), not the v1 int ids.
      body.languages = languages;
    }

    if (contentTypes && contentTypes.length > 0) {
      body.content_types = contentTypes;
    }

    if (sources && sources.length > 0) {
      body.source_ids = sources;
    }

    if (countries && countries.length > 0) {
      // v2 dropped the `source_` prefix: source_origin_country_ids -> origin_country_ids
      body.origin_country_ids = countries;
    }

    if (showOnlyBots === true) {
      // v2: source_bots -> bots
      body.bots = showOnlyBots;
    }

    if (discreditations && discreditations?.length > 0) {
      // v2: source_discreditated_entity_types -> discreditated_entity_types
      body.discreditated_entity_types = discreditations;
    }

    if (affiliationCountries?.length > 0 && affiliationCountries[0]?.value === 'all') {
      // v2: source_state_affiliated_all_countries -> state_affiliated_all_countries
      body.state_affiliated_all_countries = true;
      if (affiliationCountriesExclude) {
        excludedFilters.push('state_affiliated_all_countries');
      }
    } else if (affiliationCountries?.length > 0) {
      // v2: source_state_affiliated_country_ids -> state_affiliated_country_ids
      body.state_affiliated_country_ids = affiliationCountries?.map(v => v.value);
      if (affiliationCountriesExclude) {
        excludedFilters.push('state_affiliated_country_ids');
      }
    }

    // NOTE: feed_ids is intentionally dropped — the v2 endpoint does not accept
    // a feeds filter (no feed concept in the narrative ES index).

    if (geoGroup && geoGroup.length > 0) {
      body.country_group_ids = geoGroup;
      if (geoGroupExclude) {
        excludedFilters.push('country_group_ids');
      }
    }

    if (languagesExclude) {
      excludedFilters.push('languages');
    }

    if (countriesExclude) {
      excludedFilters.push('origin_country_ids');
    }

    if (sourceGroupsExclude) {
      excludedFilters.push('source_group_ids');
    }

    if (sourcesExclude) {
      excludedFilters.push('source_ids');
    }

    if (botsExclude) {
      excludedFilters.push('bots');
    }

    if (discreditationsExclude && Array.isArray(discreditations) && discreditations.length > 0) {
      excludedFilters.push('discreditated_entity_types');
    }

    if (affiliationTypes?.length > 0) {
      // v2 dropped the `source_` prefix: source_affiliation_types -> affiliation_types
      body.affiliation_types = toApiAffiliationTypes(affiliationTypes);
      if (affiliationTypesExclude) {
        excludedFilters.push('affiliation_types');
      }
    }

    if (excludedFilters.length > 0) {
      body.exclusive_filters = excludedFilters;
    }

    // Status / favorite / sort / pagination — moved from the v1 URL query into the body.
    if (statuses) {
      body.message_status = statuses;
    }

    if (showOnlyFavorites) {
      body.favorite = showOnlyFavorites;
    }

    if (sortingQuery) {
      body.sort = [mapSortToV2(sortingQuery)];
    }

    body.page_size = page_size;

    // Cursor-only pagination (no `page`).
    if (cursor) {
      body.cursor = cursor;
    }

    return body;
  };

  const getMessages = (
    narrative,
    searchQuery,
    sortingQuery,
    statuses,
    deduplication,
    page,
    showOnlyFavorites,
    start,
    end,
    platforms,
    sourceGroups,
    isValid,
    languages,
    sources,
    countries,
    sentiment,
    contentTypes,
    showOnlyBots,
    discreditations,
    affiliationCountries,
    feeds,
    geoGroup,
    languagesExclude,
    countriesExclude,
    sourceGroupsExclude,
    sourcesExclude,
    affiliationCountriesExclude,
    feedsExclude,
    geoGroupExclude,
    botsExclude,
    discreditationsExclude,
    tags,
    tagsExclude = false,
    cursor,
    isLoading,
    isSearchApplied,
    size=50,
    affiliationTypes = [],
    affiliationTypesExclude = false,
  ) => {
    if (!narrative) {
      return;
    }

    cancelTranslateAll();
    dispatch({type: 'BULK_TRANSLATION_LOADING', isBulkTranslationLoading: false});
    dispatch({type: 'IS_BULK_TRANSLATION', isBulkTranslation: false});
    
    const keyword = searchQuery ? searchQuery.replace(/(\r\n|\n|\r)/gm, "") : "";
    const bodyRequest = {}

    let excludedFilters = [];

    if (keyword) {
        bodyRequest['keywords'] = [keyword]
    }

    if (start) {
      bodyRequest['start_date'] = format(start, 'yyyy-LL-dd')
    }

    if (end) {
        bodyRequest['end_date'] = format(end, 'yyyy-LL-dd')
    }


    if (platforms && platforms.length > 0) {
      bodyRequest.platforms = platforms;
    }

    if (sentiment && sentiment.length > 0) {
      bodyRequest.sentiment = sentiment;
    }

    if (sourceGroups && sourceGroups.length > 0) {
      bodyRequest.source_group_ids = sourceGroups;
    }

    if(languages && languages.length > 0) {
      bodyRequest.languages = languages.map(lang => languageMapping[lang]);
    }

    if (contentTypes && contentTypes.length > 0) {
      bodyRequest.content_types = contentTypes;
    }

    if(sources && sources.length > 0) {
      bodyRequest.source_ids = sources;
    }

    if (countries && countries.length > 0) {
      bodyRequest.source_origin_country_ids = countries;
    }

    if (showOnlyBots === true) {
      bodyRequest.source_bots = showOnlyBots;
    }
    if (discreditations && discreditations?.length > 0) {
      bodyRequest.source_discreditated_entity_types = discreditations;
    }

    if(affiliationCountries?.length > 0 && affiliationCountries[0]?.value === 'all') {
      bodyRequest.source_state_affiliated_all_countries = true;
      if (affiliationCountriesExclude) {
        excludedFilters.push('source_state_affiliated_all_countries');
      }
    } else if (affiliationCountries?.length > 0) {
      bodyRequest.source_state_affiliated_country_ids = affiliationCountries?.map(v => v.value);
      if(affiliationCountriesExclude) {
        excludedFilters.push('source_state_affiliated_country_ids');
      }
    }

    if (feeds && feeds.length > 0) {
      bodyRequest.feed_ids = feeds?.map(v => v.value);
    }

    if (geoGroup && geoGroup.length > 0) {
      bodyRequest.country_group_ids = geoGroup;
      if (geoGroupExclude) {
        excludedFilters.push('country_group_ids');
      }
    }

    if (tags && tags.length > 0) {
      bodyRequest.tags = tags;
      if (tagsExclude) {
        excludedFilters.push('tags');
      }
    }

    if(languagesExclude) {
      excludedFilters.push('languages');
    }

    if(countriesExclude) {
      excludedFilters.push('source_origin_country_ids');
    }

    if(sourceGroupsExclude) {
      excludedFilters.push('source_group_ids');
    }

    if(sourcesExclude) {
      excludedFilters.push('source_ids');
    }

    if (feedsExclude) {
      excludedFilters.push('feed_ids');
    }

    if(botsExclude) {
      excludedFilters.push('source_bots');
    }

    if(discreditationsExclude && Array.isArray(discreditations) && discreditations.length > 0) {
      excludedFilters.push('source_discreditated_entity_types');
    }

    if (affiliationTypes && affiliationTypes.length > 0) {
      bodyRequest.source_affiliation_types = toApiAffiliationTypes(affiliationTypes);
      if (affiliationTypesExclude) {
        excludedFilters.push('source_affiliation_types');
      }
    }

    if(excludedFilters.length > 0) {
      bodyRequest.exclusive_filters = excludedFilters;
    }


    const urlParams = new URLSearchParams();
    urlParams.set('narrative_id', narrative.id);
    urlParams.set('size', size);

    if (cursor) {
      urlParams.set('cursor', cursor);
    }

    if (page) {
      urlParams.set('page', page);
    }

    if(statuses) {
      statuses.forEach(status => urlParams.append('message_status', status));
    }

    if(deduplication) {
      urlParams.set('deduplicated', true);
    }

    if(showOnlyFavorites) {
      urlParams.set('favorite', showOnlyFavorites);
    }

    if (sortingQuery) {
      urlParams.set('sorting', sortingQuery);
    }

    // Check if there's an existing request and abort it
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const signal = abortController.signal;

    dispatch({type: 'ERROR', error: null})
    dispatch({type: 'VALIDATION_ERROR', validationError: null})

    const requestPromise = api.fetch(
          'POST',
          `/API/v2/narratives/${narrative.id}/messages/search`,
          null,
          buildV2SearchBody({
            narrative,
            searchQuery,
            sortingQuery,
            statuses,
            page_size: size,
            showOnlyFavorites,
            start,
            end,
            platforms,
            sourceGroups,
            languages,
            sources,
            countries,
            sentiment,
            contentTypes,
            showOnlyBots,
            discreditations,
            affiliationCountries,
            geoGroup,
            languagesExclude,
            countriesExclude,
            sourceGroupsExclude,
            sourcesExclude,
            affiliationCountriesExclude,
            geoGroupExclude,
            botsExclude,
            discreditationsExclude,
            affiliationTypes,
            affiliationTypesExclude,
            cursor,
          }),
          signal
        );

    requestPromise.then(
      (data) => {

        if (cursor) {
          dispatch({type: 'APPEND_MESSAGES', newMessages: data})
        } else {
          dispatch({type: 'GET_MESSAGES', newMessages: data})
        }
         setLoading(false)

        if (isSearchApplied) {
          ampli.track({
            event_type: 'Search Perform In Case',
            event_properties: {
              keywords_filter: !!keyword,
              platforms_filter: platforms && platforms.length > 0,
              source_groups_filter: sourceGroups && sourceGroups.length > 0,
              sentiment_filter: sentiment && sentiment.length > 0,
              feeds_filter: feeds && feeds.length > 0,
              country_groups_filter: geoGroup && geoGroup.length > 0,
              languages_filter: languages && languages.length > 0,
              content_types_filter: contentTypes && contentTypes.length > 0,
              sources_filter: sources && sources.length > 0,
              origin_countries_filter: countries && countries.length > 0,
              bots_filter: showOnlyBots === true || botsExclude === true,
              discreditations_filter: discreditations && discreditations.length > 0,
              state_affiliation_filter: affiliationCountries && affiliationCountries.length > 0,
            }
          });
        }
      },
    ).catch((e) => {
      if (e.name === 'AbortError') {
        return;
      }
      if(e.cause && e.cause?.status === 412) {
        const error_message_raw = e?.message
        .replaceAll("{", "{{")
        .replaceAll("}", "}}");
        const error_parameters = e?.cause?.parameters;
        const error_message_translated = t(error_message_raw, error_parameters)
          .replaceAll("&amp;", "&")
          .replaceAll("&lt;", "<")
          .replaceAll("&gt;", ">")
          .replaceAll("&quot;", '"')
          .replaceAll("&apos;", "'");

        dispatch({type: 'VALIDATION_ERROR', validationError: error_message_translated})
        dispatch({type: 'ERROR', error: error_message_translated})
      } else {
        dispatch({type: 'ERROR', error: t(e.message)})
        showRequestError(e, t('Messages'));
      }

    });
  };

  const getStories = (
    narrative,
    searchQuery,
    start_date,
    end_date,
    statuses,
    sorting,
    page,
    size=50
  ) => {

    const urlParams = new URLSearchParams();
    urlParams.set('size', size);
    urlParams.set('page', page);

    cancelTranslateAll();
    dispatch({type: 'BULK_TRANSLATION_LOADING', isBulkTranslationLoading: false});
    dispatch({type: 'IS_BULK_TRANSLATION', isBulkTranslation: false});

    if (searchQuery) {
      urlParams.set('q', searchQuery);
    }

    if (start_date) {
      urlParams.set('start_date', format(start_date, 'yyyy-LL-dd 00:00:00'));
    }

    if (end_date) {
      urlParams.set('end_date', format(end_date, 'yyyy-LL-dd 23:59:59'));
    }

    if(statuses) {
      statuses.forEach(status => urlParams.append('message_status', status));
    }

    if (sorting) {
      if (Array.isArray(sorting.fieldName)) {
        sorting.fieldName.forEach((element) => {
          urlParams.append(
            'sorting',
            `${sorting.isAscending ? '' : '-'}${element}`,
          );
        });
      } else {
        urlParams.set('sorting', sorting);
      }
    }

    // Check if there's an existing request and abort it
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const signal = abortController.signal;

    dispatch({type: 'ERROR', error: null})
    dispatch({type: 'VALIDATION_ERROR', validationError: null})

    api.fetch('GET', `/API/v1/narrative_stories/${narrative.id}?${urlParams.toString()}`, null, null, signal)
      .then((data) => {
        dispatch({type: 'GET_MESSAGES', newMessages: data});
        
      }).catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }
        if(error.cause && error.cause?.status === 412) {
          const error_message_raw = error?.message
          .replaceAll("{", "{{")
          .replaceAll("}", "}}");
          const error_parameters = error?.cause?.parameters;
          const error_message_translated = t(error_message_raw, error_parameters)
            .replaceAll("&amp;", "&")
            .replaceAll("&lt;", "<")
            .replaceAll("&gt;", ">")
            .replaceAll("&quot;", '"')
            .replaceAll("&apos;", "'");

          dispatch({type: 'VALIDATION_ERROR', validationError: error_message_translated})
          dispatch({type: 'ERROR', error: error_message_translated})
        } else {
          let translatedMerssage = t(error.message)
          dispatch({type: 'ERROR', error: translatedMerssage})
          showRequestError(error, t('Messages'));
        }
      });
  };

  const fetchOriginalSources = (narrative, ids) => {
    if (!narrative) {
      return;
    }

    if (ids === null || ids.length === 0) {
      return;
    }

    const urlParams = new URLSearchParams();
    urlParams.set('narrative_id', narrative.id);
    urlParams.set('ids', ids)
    urlParams.set('widget_name', `original_source`);

    // TODO: migrate when narrative index supports id-based fetch
    // (this is an id-based fetch, not a search, so it stays on v1 even when
    // narrative_search_v2 is enabled).
    api.fetch('POST', `/API/v1/messages/with_search?${urlParams.toString()}`).then(
      (data) => {
        dispatch({type: 'GET_ORIGINAL_SOURCES', messages: data})
      },
    );
  }

  const fetchNarrativeStatsMessages = (narrative) => {
    const urlParams = new URLSearchParams();
    urlParams.set('widget_name', `bubble_chart_messages`);

    let narrative_id = narrative.id;

    let url;
    let dataTransformer = (data) => data;

    url = `/API/v2/narrative-widgets/${narrative_id}/bubble?${urlParams.toString()}`;
    dataTransformer = (data) => ({
      objects: [{
        id: narrative_id,
        messages: data.messages,
      }],
    });

    api.fetch('GET', url).then((data) => {
      dispatch({type: 'GET_NARRATIVE_STATS', payload: dataTransformer(data)})
    });
  }

  const fetchNarrativeStatsStories = (narrative) => {
    const urlParams = new URLSearchParams();
    
    urlParams.set('widget_name', `bubble_chart_stories`);
    api.fetch('GET', `/API/v1/dashboard/narrative_stories/${narrative.id}/sentiment?${urlParams.toString()}`,).then((data) => {
      dispatch({type: 'GET_NARRATIVE_STATS_STORIES', payload: data})
    });
  }

  const fetchMostMentionedEntities = (narrative, entity_type) => {
    const urlParams = new URLSearchParams();

    urlParams.set('entity_type', entity_type)

    dispatch({type: 'DELETE_ENTITIES'})

    api.fetch(
      'GET',
      `/API/v1/dashboard/narratives/${narrative.id}/entities?${urlParams.toString()}`,
    )
    .then((data) => {
      dispatch({type: 'GET_MOST_MENTIONED_ENTITIES', payload: data})
    });
  }

  const fetchNarrativeShares = (narrative, statsAggregation, statsBreakdown) => {
    dispatch({type: 'GET_NARRATIVE_SHARES', payload: null})
    const urlParams = new URLSearchParams();
    urlParams.append('narrative_id', narrative.id)
    if (statsAggregation) { 
      urlParams.set('aggregation', statsAggregation);
    }
    urlParams.set('widget_name', `evolution_chart`);

    urlParams.set('include_unclassified', false);
    urlParams.set('top', 6);

    if (statsBreakdown !== null) {
      urlParams.set('breakdown', statsBreakdown);
    }

    let url = `/API/v2/narrative-widgets/${narrative.id}/evolution?${urlParams.toString()}`;

    api.fetch('GET', url)
    .then((data) => {
      dispatch({type: 'GET_NARRATIVE_SHARES', payload: data})
    });
    }

  const fetchNarrativeSetnimentStats = (narrative) => {
    const urlParams = new URLSearchParams();
    urlParams.append('narrative_id', narrative.id);

    api.fetch('GET', `/API/v2/narrative-widgets/${narrative.id}/sentiment`)
    .then((data) => {
      dispatch({type: 'GET_NARRATIVE_SENTIMENT_STATS', payload: data})
    });
  }

  const deleteMessages = (narrative, ids, deduplication=false, deleteFromCase = true) => {
    let headers = { 'Content-Type': 'application/json' };

    if(narrative.is_threat && currentUser.is_super_admin && narrative.workspace_id !== currentUser.workspace_id) {
      headers['workspace-id'] = narrative.workspace_id;
    }

    let body = {
      delete_from_case: deleteFromCase,
    };

    if (deduplication) {
      body['narrative_story_ids'] = ids;
    } else {
      body['message_ids'] = ids;
    }

    return api.fetch('DELETE', `/API/v1/narratives/${narrative.id}/messages`, headers, body);
  };

  const handleAllMessages = (
    narrative,
    searchQuery,
    statuses,
    showOnlyFavorites,
    start,
    end,
    platforms,
    sourceGroups,
    languages,
    sources,
    countries,
    sentiment,
    contentTypes,
    showOnlyBots,
    discreditations,
    affiliationCountries,
    feeds,
    geoGroup,
    languagesExclude,
    countriesExclude,
    sourceGroupsExclude,
    sourcesExclude,
    affiliationCountriesExclude,
    feedsExclude,
    geoGroupExclude,
    botsExclude,
    discreditationsExclude,
    tags,
    status,
    to_narrativeId,
    messageIds,
    affiliationTypes = [],
    affiliationTypesExclude = false,
  ) => {
    let headers = { 'Content-Type': 'application/json' };

    if(narrative.is_threat && currentUser.is_super_admin && narrative.workspace_id !== currentUser.workspace_id) {
      headers['workspace-id'] = narrative.workspace_id;
    }

    const keyword = searchQuery ? searchQuery.replace(/(\r\n|\n|\r)/gm, "") : "";
    const bodyRequest = {}

    if (keyword) {
        bodyRequest['keywords'] = [keyword]
    }

    if (start) {
      bodyRequest['start_date'] = format(start, 'yyyy-LL-dd')
    }

    if (end) {
        bodyRequest['end_date'] = format(end, 'yyyy-LL-dd')
    }


    if (platforms && platforms.length > 0) {
      bodyRequest.platforms = platforms;
    }

    if (sentiment && sentiment.length > 0) {
      bodyRequest.sentiment = sentiment;
    }

    if (sourceGroups && sourceGroups.length > 0) {
      bodyRequest.source_group_ids = sourceGroups;
    }

    if(languages && languages.length > 0) {
      bodyRequest.languages = languages.map(lang => languageMapping[lang]);
    }

    if (contentTypes && contentTypes.length > 0) {
      bodyRequest.content_types = contentTypes;
    }

    if(sources && sources.length > 0) {
      bodyRequest.source_ids = sources;
    }

    if (countries && countries.length > 0) {
      bodyRequest.source_origin_country_ids = countries;
    }

    if (showOnlyBots === true) {
      bodyRequest.source_bots = showOnlyBots;
    }

    if (discreditations && discreditations?.length > 0) {
      bodyRequest.source_discreditated_entity_types = discreditations;
    }

    if(affiliationCountries?.length > 0 && affiliationCountries[0]?.value === 'all') {
      bodyRequest.source_state_affiliated_all_countries = true;
    } else {
      bodyRequest.source_state_affiliated_country_ids = affiliationCountries?.map(v => v.value);
    }

    if (feeds && feeds.length > 0) {
      bodyRequest.feed_ids = feeds?.map(v => v.value);
    }

    if (geoGroup && geoGroup.length > 0) {
      bodyRequest.country_group_ids = geoGroup;
    }

    if (tags && tags.length > 0) {
      bodyRequest.tags = tags;
    }

    let excludedFilters = [];

    if (geoGroupExclude) {
      excludedFilters.push('country_group_ids');
    }

    if(languagesExclude) {
      excludedFilters.push('languages');
    }
    
    if(countriesExclude) {
      excludedFilters.push('source_origin_country_ids');
    }

    if(sourceGroupsExclude) {
      excludedFilters.push('source_group_ids');
    }

    if(sourcesExclude) {
      excludedFilters.push('source_ids');
    }

    if(affiliationCountriesExclude) {
      excludedFilters.push('source_state_affiliated_country_ids');
    }

    if (feedsExclude) {
      excludedFilters.push('feed_ids');
    }

    if(botsExclude) {
      excludedFilters.push('source_bots');
    }

    if(discreditationsExclude && Array.isArray(discreditations) && discreditations.length > 0) {
      excludedFilters.push('source_discreditated_entity_types');
    }

    if (affiliationTypes && affiliationTypes.length > 0) {
      bodyRequest.source_affiliation_types = toApiAffiliationTypes(affiliationTypes);
      if (affiliationTypesExclude) {
        excludedFilters.push('source_affiliation_types');
      }
    }

    if(excludedFilters.length > 0) {
      bodyRequest.exclusive_filters = excludedFilters;
    }


    const urlParams = new URLSearchParams();
    urlParams.set('narrative_id', narrative.id);

    if(status !== null) {
      urlParams.set('status', status);
    }

    if(statuses) {
      statuses.forEach(status => urlParams.append('message_status', status));
    }

    if(showOnlyFavorites) {
      urlParams.set('favorite', showOnlyFavorites);
    }

    if (messageIds) {
      urlParams.set('ids', messageIds);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const signal = abortController.signal;

    let urlNarrativeId = to_narrativeId ? to_narrativeId : narrative.id;

    return api.fetch('POST', `/API/v1/narratives/${urlNarrativeId}/messages/from_search?${urlParams.toString()}`, headers, bodyRequest, signal)
  }        

  const patchMessages = (narrative, status, ids, deduplication=false) => {
    let headers = { 'Content-Type': 'application/json' };

    if(narrative.is_threat && currentUser.is_super_admin && narrative.workspace_id !== currentUser.workspace_id) {
      headers['workspace-id'] = narrative.workspace_id;
    }

    let body;

    if (deduplication) {
      body = {
        status: status,
        narrative_story_ids: ids,
      }
    } else {
      body = {
        status: status,
        message_ids: ids
      }
    }

    return api.fetch('PATCH', `/API/v1/narratives/${narrative.id}/messages`, headers, body);
  }

  const editBubbleChartSentiment = (sentiment, messageId, narrative) => {
    api.fetch('PATCH', `/API/v1/narrative_stories/${narrative.id}/story/${messageId}`, null, {sentiment: sentiment}).then(data => {
      fetchNarrativeStatsStories(narrative)
    })
  }

  const toggleFavotireMessage = (narrative, favorite, ids, deduplication=false) => {
    let body;

    if (deduplication) {
      body = {
        favorite: favorite,
        narrative_story_ids: ids,
      }
    } else {
      body = {
        favorite: favorite,
        message_ids: ids
      }
    }

    dispatch({type: 'TOGGLE_FAVORITE', payload: {ids: ids, favorite: favorite}});

    return api.fetch('PATCH', `/API/v1/narratives/${narrative.id}/messages`, null, body);
  }

  const toggleAll = (messages, selectedMessages) => {
      if(areAllIdsSelected(messages.objects, selectedMessages)) {
        dispatch({type: 'DESELECT_ALL_MESSAGES'})
      } else {
        if(state.deduplication) {
          const newSelectedMessages = Object.fromEntries(
            messages.objects.map(message => [message.id, true])
          );
          dispatch({type: 'SELECT_ALL_MESSAGES', payload: newSelectedMessages});
        } else {
          const newSelectedMessages = Object.fromEntries(
            messages.objects.map(message => [message.id, true])
          );
          dispatch({type: 'SELECT_ALL_MESSAGES', payload: newSelectedMessages});
        }
        
      }
  }

  const toggleSelectAllMessagesInCase = (bool) => {
    dispatch({type: 'TOGGLE_SELECT_ALL_CASE_MESSAGES', payload: bool})
  }

  const clearAllSelectedMessages = () => {
    dispatch({type: 'CLEAR_ALL_SELECTED_MESSAGES'})
  }

  const translateMessage = (messageId, text, nativeLang, sourceLang='', abortController) => {
    let body;
    if(sourceLang) {
      body = {
        text: text,
        destination_language: nativeLang,
        source_language: sourceLang
      }
    } else {
      body = {
        text: text,
        destination_language: nativeLang,
      }
    }
    return api.fetch('POST', '/API/v1/translations/translate', null, body, abortController?.current?.signal)
  }

  const handleBulkTranslation = async (messagesToTranslate, nativeLanguage, abortController) => {
    const BATCH_SIZE = 5;
    
    const batches = [];
    for (let i = 0; i < messagesToTranslate.length; i += BATCH_SIZE) {
      batches.push(messagesToTranslate.slice(i, i + BATCH_SIZE));
    }

    for (const batch of batches) {
      if (abortController?.current.aborted) {
        break;
      }
      
      try {
        await processBatch(batch, nativeLanguage, abortController);
      } catch (error) {
        if (error.name === 'AbortError') {
          break;
        }
        console.error('Error processing batch:', error);
        // Continue with next batch even if current batch fails
      }
    }
  };

  const processBatch = async (batch, nativeLanguage, abortController = null) => {
    if (!state.messages || !state.messages.objects) {
      return;
    }

    if (abortController?.current.aborted) {
      return;
    }

    const translationPromises = batch.map(message => {
      const originalPostText = message.content_type === 'COMMENT' ? replaceNewlinesWithBreaks(message.parent_message?.highlighted_text || message.parent_message?.text) : '';
      let text = state.deduplication ? replaceNewlinesWithBreaks(message.summary) : message.highlighted_text ? replaceNewlinesWithBreaks(message.highlighted_text) : replaceNewlinesWithBreaks(message.text);
      if (originalPostText) {
        return Promise.allSettled([
          translateMessage(message.id, originalPostText, nativeLanguage, null, abortController),
          translateMessage(message.id, text, nativeLanguage, null, abortController),
        ]).then(results => ({
          messageId: message.id,
          type: 'comment',
          parentResult: results[0],
          messageResult: results[1]
        }));
      } else {
        return translateMessage(message.id, text, nativeLanguage, null, abortController)
          .then(data => ({
            messageId: message.id,
            type: 'message',
            messageResult: { status: 'fulfilled', value: data }
          }))
          .catch(error => ({
            messageId: message.id,
            type: 'message',
            messageResult: { status: 'rejected', reason: error }
          }));
      }
    });

    const batchResults = await Promise.allSettled(translationPromises);

    if (abortController?.current?.aborted) {
      return;
    }

    dispatch({type: 'BULK_TRANSLATION', payload: {batchResults, t}});
  };

  const toggleAllToTranslation = (messageIds) => {
    dispatch({type: 'TOGGLE_ALL_TO_TRANSLATION', payload: messageIds})
  };

  const toggleAllToOriginal = (messageIds) => {
    dispatch({type: 'TOGGLE_ALL_TO_ORIGINAL', payload: messageIds})
  };

  
  
  const handleBulkTranslateClick = async (nativeLang) => {
    if (!state.messages || state?.messages?.objects?.length === 0) {
      dispatch({type: 'IS_BULK_TRANSLATION', isBulkTranslation: false});
      dispatch({type: 'BULK_TRANSLATION_LOADING', isBulkTranslationLoading: false});
      return;
    }

    if (translationControllerRef.current) {
      translationControllerRef.current.abort();
    }

    translationControllerRef.current = new AbortController();

    dispatch({type: 'BULK_TRANSLATION_LOADING', isBulkTranslationLoading: true});
    
    const messagesToTranslate = [];
    const alreadyTranslated = [];

    state?.messages?.objects?.forEach(message => {
      if (message.translated) {
        alreadyTranslated.push(message);
      } else {
        messagesToTranslate.push(message);
      }
    });
    
    if (messagesToTranslate?.length === 0) {
      console.log('No messages to translate');
      const messageIds = alreadyTranslated?.map(msg => msg.id);
      toggleAllToTranslation(messageIds);
      dispatch({type: 'BULK_TRANSLATION_LOADING', isBulkTranslationLoading: false});
      return;
    }

    if ( alreadyTranslated.length > 0) {
      toggleAllToTranslation(alreadyTranslated.map(msg => msg.id));
    }

    const messageIds = messagesToTranslate.map(msg => msg.id);
    toggleAllToTranslation(messageIds);


    try {
      await handleBulkTranslation(messagesToTranslate, nativeLang, translationControllerRef);
      dispatch({type: 'BULK_TRANSLATION_LOADING', isBulkTranslationLoading: false});
    } catch (error) {
      dispatch({type: 'BULK_TRANSLATION_LOADING', isBulkTranslationLoading: false});
    } finally {
      translationControllerRef.current = null;
    }
  };

  const cancelTranslateAll = () => {
    if (translationControllerRef.current) {
      translationControllerRef.current.abort();
    }
  };

  const handleShowOriginalClick = () => {
    cancelTranslateAll();
    const translatedMessages = state.messages.objects.filter(message => message.translated);
    const messageIds = translatedMessages.map(msg => msg.id);
    
    toggleAllToOriginal(messageIds);
    dispatch({type: 'BULK_TRANSLATION_LOADING', isBulkTranslationLoading: false});
  };

  const createStories = (narrative, confirmQuotaUsage) => {
    return api.fetch('POST', `/API/v1/narratives/${narrative.id}/enable_stories?confirm_quota_usage=${confirmQuotaUsage}`)
  };

  const toggleTranslate = (messageId, isTranslation) => {
    dispatch(
      {type: 'TOGGLE_TRANSLATE', payload: {messageId: messageId, isTranslation: isTranslation}}
    )
  }

  const toggleSelected = (messageId) => {
    dispatch({type: 'TOGGLE_SELECTED', messageId: messageId })
  }

  const toggleDeduplication = (bool) => {
    dispatch({type:'TOGGLE_DEDUPLICATION', payload: bool})
  }

  const toggleTab = () => {
    dispatch({type:'TOGGLE_TAB'})
  }

  const clearSelectedMessages = () => {
    dispatch({type:'PATCH_MESSAGES'})
  }

  const applySearch = (bool) => {
    dispatch({type: 'APPLY_SEARCH', isSearchApplied: bool})
  }

  const setLoading = (bool) => {
    dispatch({type: 'SET_LOADING', isLoading: bool})
  }

  const cancelRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }

  const setStoriesMessage = (bool) => {
    dispatch({type: 'SHOW_STORIES_MESSAGE', showStoriesMessage: bool})
  }

  const value = {
    narrative: state.narrative,
    messages: state.messages,
    narrativeShares: state.narrativeShares,
    narrativeStats: state.narrativeStats,
    narrativeStatsStories: state.narrativeStatsStories,
    narrativeSentimentStats: state.narrativeSentimentStats,
    mostMentionedEntities: state.mostMentionedEntities,
    error: state.error,
    selectedMessages: state.selectedMessages,
    deduplication: state.deduplication,
    isSearchApplied: state.isSearchApplied,
    isLoading: state.isLoading,
    originalSources: state.originalSources,
    showStoriesMessage: state.showStoriesMessage,
    areAllMessagesSelected: state.areAllMessagesSelected,
    validationError: state.validationError,
    isBulkTranslationLoading: state.isBulkTranslationLoading,
    isBulkTranslation: state.isBulkTranslation,

    dispatch: dispatch,
    fetchNarrativeStatsMessages: fetchNarrativeStatsMessages,
    fetchNarrativeStatsStories: fetchNarrativeStatsStories,
    fetchNarrativeShares: fetchNarrativeShares,
    fetchMostMentionedEntities: fetchMostMentionedEntities,
    fetchNarrativeSetnimentStats: fetchNarrativeSetnimentStats,
    editBubbleChartSentiment: editBubbleChartSentiment,
    getMessages: getMessages,
    getStories: getStories,
    patchMessages: patchMessages,
    toggleFavotireMessage: toggleFavotireMessage,
    deleteMessages: deleteMessages,
    toggleAll: toggleAll,
    fetchOriginalSources: fetchOriginalSources,
    toggleSelected: toggleSelected,
    toggleDeduplication: toggleDeduplication,
    clearSelectedMessages: clearSelectedMessages,
    toggleTranslate: toggleTranslate,
    translateMessage: translateMessage,
    toggleTab: toggleTab,
    clearAllSelectedMessages: clearAllSelectedMessages,
    applySearch: applySearch,
    setLoading: setLoading,
    cancelRequest: cancelRequest,
    createStories: createStories,
    fetchNarrative: fetchNarrative,
    setStoriesMessage: setStoriesMessage,
    toggleSelectAllMessagesInCase: toggleSelectAllMessagesInCase,
    handleAllMessages: handleAllMessages,
    toggleAllToOriginal: toggleAllToOriginal,
    toggleAllToTranslation: toggleAllToTranslation,
    handleBulkTranslateClick: handleBulkTranslateClick,
    handleShowOriginalClick: handleShowOriginalClick,
    cancelTranslateAll: cancelTranslateAll,
  }

  return (
    <MessagesContext.Provider value={value}>
      {children}
    </MessagesContext.Provider>
  )
}

